import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { blogPosts } from '../data/mock';
import { Search, Calendar, Tag, MapPin } from 'lucide-react';

const Blog = () => {
  const [searchParams] = useSearchParams();
  const regionParam = searchParams.get('bolge');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [selectedRegion, setSelectedRegion] = useState('Tümü');

  useEffect(() => {
    if (regionParam) {
      // Convert URL param back to region name
      const regionMap = {
        'bahcesehir': 'Bahçeşehir',
        'beylikduzu': 'Beylikdüzü',
        'esenyurt': 'Esenyurt',
        'basaksehir': 'Başakşehir',
        'buyukcekmece-alkent': 'Büyükçekmece'
      };
      setSelectedRegion(regionMap[regionParam] || 'Tümü');
    }
  }, [regionParam]);

  const categories = ['Tümü', 'Bakım İpuçları', 'Leke Çıkarma', 'Halı Türleri', 'Bölge Rehberi'];
  const regions = ['Tümü', 'Genel', 'Bahçeşehir', 'Beylikdüzü', 'Esenyurt', 'Başakşehir', 'Büyükçekmece'];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Tümü' || post.category === selectedCategory;
    const matchesRegion = selectedRegion === 'Tümü' || post.region === selectedRegion || post.region === 'Genel';
    return matchesSearch && matchesCategory && matchesRegion;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">Blog</h1>
          <p className="text-xl text-teal-50 max-w-3xl mx-auto">
            Halı bakımı, temizlik ipuçları ve daha fazlası için blog yazılarımızı okuyun.
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-4">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Blog yazılarında ara..."
                className="pl-10 h-12"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Region Filter */}
            {regionParam && (
              <div className="flex items-center gap-2 bg-teal-50 px-4 py-2 rounded-lg inline-flex w-fit">
                <MapPin className="w-5 h-5 text-teal-600" />
                <span className="text-sm font-medium text-teal-700">
                  {selectedRegion} bölgesine özel yazılar
                </span>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-semibold text-gray-700 py-2">Kategori:</span>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-2">
              <span className="text-sm font-semibold text-gray-700 py-2">Bölge:</span>
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => setSelectedRegion(region)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedRegion === region
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {region}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">Aradığınız kriterlere uygun blog yazısı bulunamadı.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="overflow-hidden group hover:shadow-2xl transition-all h-full border-2 hover:border-teal-500">
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-teal-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                          {post.category}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(post.date).toLocaleDateString('tr-TR')}</span>
                        </div>
                        <span className="text-teal-600 font-medium group-hover:underline">Devamını Oku →</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Blog Güncellemelerini Kaçırmayın!</h2>
          <p className="text-teal-50 mb-8 max-w-2xl mx-auto">
            Yeni blog yazılarımızdan haberdar olmak için bizi takip edin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <Input
              type="email"
              placeholder="E-posta adresiniz"
              className="h-12 bg-white text-gray-900"
            />
            <button className="bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Abone Ol
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
